import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import api from "../../lib/api";
import { toast } from "sonner";
import { Pin, Send, Mail, Phone, MessageSquare, Wallet as WalletIcon, FileText, ChevronDown, ChevronRight, ShieldCheck, ShieldX, ShieldQuestion, Clock as ClockIcon, Copy as CopyIcon, AlertTriangle, Download } from "lucide-react";

const KYCAID_STATE_STYLES = {
  verified:   { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: ShieldCheck, label: "Verified" },
  submitted:  { cls: "bg-sky-50 text-sky-700 border-sky-200",             Icon: ClockIcon,   label: "Submitted" },
  failed:     { cls: "bg-rose-50 text-rose-700 border-rose-200",          Icon: ShieldX,     label: "Failed" },
  unverified: { cls: "bg-slate-100 text-slate-600 border-slate-200",      Icon: ShieldQuestion, label: "Not started" },
};

const formatEUR = (v) => `€${(v || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminCustomer360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState({ body: "", pinned: false });
  const [comm, setComm] = useState({ channel: "email", direction: "out", subject: "", body: "" });
  const [openOrder, setOpenOrder] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/admin/customers/${id}/360`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const addNote = async () => {
    if (!note.body.trim()) return;
    try {
      await api.post(`/admin/customers/${id}/notes`, note);
      toast.success("Note added");
      setNote({ body: "", pinned: false });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const addComm = async () => {
    if (!comm.body.trim()) return;
    try {
      await api.post(`/admin/customers/${id}/comms`, comm);
      toast.success("Logged");
      setComm({ ...comm, subject: "", body: "" });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <AdminLayout title="Customer"><div className="text-sm text-muted-foreground">Loading…</div></AdminLayout>;
  if (!data) return <AdminLayout title="Customer"><div className="text-sm text-muted-foreground">Not found.</div></AdminLayout>;

  const c = data.customer;
  const k = data.kycaid || { state: "unverified" };
  const kStyle = KYCAID_STATE_STYLES[k.state] || KYCAID_STATE_STYLES.unverified;
  const KIcon = kStyle.Icon;

  return (
    <AdminLayout
      title={c.name || c.email}
      subtitle={`${c.email} · ${c.country} · KYCAID ${kStyle.label.toLowerCase()}`}
      actions={
        <Button onClick={() => navigate("/admin/customers")} variant="outline" size="sm" className="rounded-xl">
          ← Back to list
        </Button>
      }
    >
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3" data-testid="c360-stats">
        <Stat label="Spent (EUR)" value={formatEUR(data.totals.spent_eur)} testid="stat-spent" />
        <Stat label="Orders" value={data.totals.orders_total} testid="stat-orders" />
        <Stat label="Completed" value={data.totals.orders_completed} testid="stat-completed" />
        <Stat label="KYCAID" value={kStyle.label} testid="stat-kycaid" />
      </div>

      {/* Cumulative spend windows + SoF threshold warnings (5AMLD trigger surface) */}
      <CumulativeSpend totals={data.totals} />

      {/* Provider IDs — the customer's stable XGate handle (used for invoice lookup) */}
      <ProviderIds customer={c} kycaid={k} />

      {/* KYCAID (Phase 0) operational gate — staff must see this before manually issuing an XGate invoice */}
      <div className="xch-card p-4 mb-5" data-testid="c360-kycaid-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 border ${kStyle.cls}`}>
            <KIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-[280px]">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-foreground">KYCAID document verification</div>
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-bold ${kStyle.cls}`} data-testid="kycaid-state-badge">
                {k.state}
              </Badge>
              {k.state === "verified" && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 border-emerald-300">
                  Cleared to invoice
                </Badge>
              )}
              {k.state !== "verified" && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-800 border-amber-200">
                  Do not invoice yet
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mt-2 text-xs">
              <div className="text-muted-foreground">Applicant ID</div>
              <div className="font-mono text-[11px] text-foreground truncate" data-testid="kycaid-applicant-id">{k.applicant_id || "—"}</div>
              <div className="text-muted-foreground">Verification ID</div>
              <div className="font-mono text-[11px] text-foreground truncate" data-testid="kycaid-verification-id">{k.verification_id || "—"}</div>
              <div className="text-muted-foreground">Last callback</div>
              <div className="text-foreground">{k.last_callback_at ? new Date(k.last_callback_at).toLocaleString("en-IE") : "—"}</div>
              <div className="text-muted-foreground">Callback type</div>
              <div className="text-foreground">{k.callback_type || "—"}</div>
              <div className="text-muted-foreground">Document check</div>
              <div className="text-foreground">{renderCheck(k.check_document)}</div>
              <div className="text-muted-foreground">Address check</div>
              <div className="text-foreground">{renderCheck(k.check_address)}</div>
            </div>
            {(k.decline_reasons || []).length > 0 && (
              <div className="mt-3 rounded-xl bg-rose-50 border border-rose-200 p-2.5 text-[11px] text-rose-900" data-testid="kycaid-decline">
                <strong>Decline reasons:</strong>
                <ul className="mt-1 list-disc pl-4 space-y-0.5">
                  {k.decline_reasons.map((r, i) => <li key={i}>{String(r)}</li>)}
                </ul>
              </div>
            )}
            {(k.recent_callbacks || []).length > 0 && (
              <details className="mt-3 text-[11px]" data-testid="kycaid-recent-callbacks">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  Recent callbacks ({k.recent_callbacks.length})
                </summary>
                <div className="mt-2 space-y-1.5">
                  {k.recent_callbacks.map((cb, i) => (
                    <div key={i} className="rounded-lg border border-border bg-muted/30 p-2 font-mono text-[10px] text-foreground">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <span>{new Date(cb.received_at).toLocaleString("en-IE")}</span>
                        <Badge variant="outline" className="text-[9px] uppercase font-bold">{cb.type || "—"}</Badge>
                        <Badge variant="outline" className="text-[9px] uppercase font-bold">→ {cb.state_after || "?"}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="orders" className="w-full">
        <TabsList className="bg-muted/40 rounded-xl mb-4" data-testid="c360-tabs">
          <TabsTrigger value="orders" data-testid="tab-orders">Orders ({data.orders.length})</TabsTrigger>
          <TabsTrigger value="wallets" data-testid="tab-wallets">Wallets ({data.wallets.length})</TabsTrigger>
          <TabsTrigger value="sof" data-testid="tab-sof">SoF</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes ({data.notes.length})</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comms">Comms ({data.comms.length})</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit ({data.audit.length})</TabsTrigger>
          <TabsTrigger value="flags" data-testid="tab-flags">Flags ({data.flags.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          <div className="xch-card overflow-hidden" data-testid="orders-list">
            {data.orders.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No orders.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.orders.map((o) => {
                  const open = openOrder === o.id;
                  return (
                  <div key={o.id} data-testid={`order-${o.id}`}>
                    <button onClick={() => setOpenOrder(open ? null : o.id)}
                            className="w-full text-left p-4 flex items-center gap-3 hover:bg-muted/30">
                      {open ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[11px] text-muted-foreground">{o.reference}</div>
                        <div className="font-display font-bold text-foreground">
                          {formatEUR(o.fiat_amount)} → {o.receive_amount?.toFixed(6)} {o.asset}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(o.created_at).toLocaleString("en-IE")} · {o.network}
                        </div>
                      </div>
                      <Badge variant="outline" className="rounded-full text-[10px] uppercase font-bold tracking-wider">
                        {o.status?.replace(/_/g, " ")}
                      </Badge>
                    </button>
                    {open && (
                      <div className="px-4 pb-4 bg-muted/20 border-t border-border" data-testid={`order-detail-${o.id}`}>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2 mt-3">3-leg ledger</div>
                        {(o.ledger || []).length === 0 ? (
                          <div className="text-xs text-muted-foreground">No ledger entries yet.</div>
                        ) : (
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                <th className="text-left py-1">Leg</th>
                                <th className="text-right py-1">Amount</th>
                                <th className="text-left py-1 pl-3">Status</th>
                                <th className="text-left py-1 pl-3">Ref</th>
                              </tr>
                            </thead>
                            <tbody>
                              {o.ledger.map((e) => (
                                <tr key={e.id} className="border-t border-border/50">
                                  <td className="py-1.5 font-mono">{e.leg}</td>
                                  <td className="py-1.5 text-right font-mono tabular-nums">
                                    {e.amount.toLocaleString("en-IE", { maximumFractionDigits: 4 })} {e.currency}
                                  </td>
                                  <td className="py-1.5 pl-3">
                                    <Badge variant="outline" className={`text-[9px] uppercase font-bold ${e.status === "posted" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                                      {e.status}
                                    </Badge>
                                  </td>
                                  <td className="py-1.5 pl-3 font-mono text-[10px] text-muted-foreground truncate max-w-[200px]">{e.ref || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    )}
                  </div>
                );})}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="wallets">
          <div className="xch-card overflow-hidden" data-testid="wallets-list">
            {data.wallets.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No wallets.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.wallets.map((w) => (
                  <div key={w.id} className="p-4 flex items-center gap-3" data-testid={`c360-wallet-${w.id}`}>
                    <WalletIcon className="w-5 h-5 text-primary" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-foreground flex items-center gap-2 flex-wrap">
                        {w.label} <Badge variant="outline" className="text-[10px] uppercase font-bold">{w.network}</Badge>
                        <Badge variant="outline" className={`text-[10px] uppercase font-bold ${w.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : w.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                          {w.status || "approved"}
                        </Badge>
                      </div>
                      <div className="font-mono text-[11px] text-muted-foreground truncate">{w.address}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="sof">
          <SofTab customerId={c.id} customer={c} />
        </TabsContent>

        <TabsContent value="notes">
          <div className="xch-card p-4 mb-4" data-testid="note-form">
            <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Add note</Label>
            <textarea value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })}
                      placeholder="Operator note (visible to all staff)…"
                      className="w-full mt-2 px-3 py-2 rounded-xl border border-border bg-background text-sm min-h-[80px]"
                      data-testid="note-body-input" />
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={note.pinned}
                       onChange={(e) => setNote({ ...note, pinned: e.target.checked })} />
                Pin
              </label>
              <Button onClick={addNote} disabled={!note.body.trim()}
                      className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                      data-testid="note-add-btn">
                <Pin className="w-3.5 h-3.5" /> Add note
              </Button>
            </div>
          </div>
          <div className="xch-card overflow-hidden" data-testid="notes-list">
            {data.notes.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No notes yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.notes.map((n) => (
                  <div key={n.id} className="p-3" data-testid={`note-${n.id}`}>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <strong className="text-foreground">{n.author_email}</strong> · {new Date(n.ts).toLocaleString("en-IE")}
                      {n.pinned && <Pin className="w-3 h-3 text-amber-600" />}
                    </div>
                    <div className="text-sm text-foreground mt-1 whitespace-pre-wrap">{n.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="comms">
          <div className="xch-card p-4 mb-4" data-testid="comm-form">
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Select value={comm.channel} onValueChange={(v) => setComm({ ...comm, channel: v })}>
                <SelectTrigger className="rounded-xl" data-testid="comm-channel-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="chat">Chat</SelectItem>
                  <SelectItem value="ticket">Ticket</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select value={comm.direction} onValueChange={(v) => setComm({ ...comm, direction: v })}>
                <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="out">Outbound</SelectItem>
                  <SelectItem value="in">Inbound</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Subject (optional)" value={comm.subject}
                     onChange={(e) => setComm({ ...comm, subject: e.target.value })}
                     className="rounded-xl" />
            </div>
            <textarea placeholder="Body…" value={comm.body}
                      onChange={(e) => setComm({ ...comm, body: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm min-h-[80px]"
                      data-testid="comm-body-input" />
            <div className="flex justify-end mt-2">
              <Button onClick={addComm} disabled={!comm.body.trim()}
                      className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5"
                      data-testid="comm-add-btn">
                <Send className="w-3.5 h-3.5" /> Log
              </Button>
            </div>
          </div>
          <div className="xch-card overflow-hidden" data-testid="comms-list">
            {data.comms.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No communications logged.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.comms.map((c) => {
                  const Icon = c.channel === "phone" ? Phone : c.channel === "chat" ? MessageSquare : Mail;
                  return (
                    <div key={c.id} className="p-3" data-testid={`comm-${c.id}`}>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <Icon className="w-3 h-3" />
                        <Badge variant="outline" className="text-[9px] uppercase font-bold">{c.direction}</Badge>
                        <span><strong className="text-foreground">{c.author_email}</strong> · {new Date(c.ts).toLocaleString("en-IE")}</span>
                      </div>
                      {c.subject && <div className="text-sm font-semibold text-foreground mt-1">{c.subject}</div>}
                      <div className="text-sm text-foreground mt-1 whitespace-pre-wrap">{c.body}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="audit">
          <div className="xch-card overflow-hidden" data-testid="audit-list">
            {data.audit.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No audit entries.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.audit.slice(0, 50).map((a) => (
                  <div key={a.id} className="p-3 text-xs">
                    <div className="font-mono text-[10px] text-muted-foreground">{new Date(a.ts).toLocaleString("en-IE")}</div>
                    <div className="text-sm text-foreground font-semibold">{a.action}</div>
                    <div className="text-[11px] text-muted-foreground">by <strong>{a.actor_email}</strong> · {a.reason || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="flags">
          <div className="xch-card overflow-hidden" data-testid="flags-list">
            {data.flags.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No compliance flags.</div>
            ) : (
              <div className="divide-y divide-border">
                {data.flags.map((f) => (
                  <div key={f.id} className="p-3 flex items-center gap-3" data-testid={`flag-${f.id}`}>
                    <FileText className="w-4 h-4 text-rose-700" />
                    <div className="flex-1">
                      <div className="font-semibold text-foreground">{f.reason}</div>
                      <div className="text-[11px] text-muted-foreground">{new Date(f.created_at).toLocaleString("en-IE")} · {f.status}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="xch-card p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="font-display text-lg font-extrabold text-foreground tabular-nums truncate">{value}</div>
  </div>
);

function renderCheck(v) {
  if (v === true || v === "passed" || v === "approved" || v === "valid") {
    return <span className="text-emerald-700 font-semibold">passed</span>;
  }
  if (v === false || v === "failed" || v === "rejected" || v === "invalid") {
    return <span className="text-rose-700 font-semibold">failed</span>;
  }
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  return <span className="text-foreground">{String(v)}</span>;
}


/* ----- Provider IDs (XGate / KYCAID handles) ----- */
function ProviderIds({ customer, kycaid }) {
  const xgateId = customer.xgate_customer_id;
  const applicantId = kycaid?.applicant_id;
  const copy = async (val, label) => {
    if (!val) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(val);
      } else {
        // Fallback for restricted contexts (sandboxed iframes, http, older browsers)
        const ta = document.createElement("textarea");
        ta.value = val;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error(`${label} — copy failed, select and copy manually`);
    }
  };
  return (
    <div className="xch-card p-4 mb-5" data-testid="c360-provider-ids">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
        Provider IDs · use these to look up the customer in XGate / KYCAID portals
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <IdRow
          label="XGate customerId"
          value={xgateId}
          hint="Match repeat customers in the XGate back-office. Also used in callback signatures."
          onCopy={() => copy(xgateId, "XGate customerId")}
          testid="xgate-customer-id"
        />
        <IdRow
          label="KYCAID applicant_id"
          value={applicantId}
          hint="One per user, never regenerated. Use to find them in the KYCAID dashboard."
          onCopy={() => copy(applicantId, "KYCAID applicant_id")}
          testid="kycaid-applicant-id-copy"
        />
      </div>
    </div>
  );
}

function IdRow({ label, value, hint, onCopy, testid }) {
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
        {value ? (
          <Button onClick={onCopy} variant="ghost" size="sm"
                  className="h-6 px-2 rounded-md text-[10px] gap-1 ml-auto"
                  data-testid={`${testid}-copy-btn`}>
            <CopyIcon className="w-3 h-3" /> Copy
          </Button>
        ) : null}
      </div>
      <div className="font-mono text-[11px] text-foreground mt-1 truncate" data-testid={`${testid}-value`}>
        {value || "—"}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

/* ----- Cumulative spend windows + SoF threshold warnings ----- */
function CumulativeSpend({ totals }) {
  const t = totals.sof_thresholds || {};
  const cumBreached = !!t.cumulative_12m_breached;
  const singleBreached = !!t.single_order_breached;
  return (
    <div className="xch-card p-4 mb-3" data-testid="c360-cumulative-spend">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
        Spend windows (completed orders only)
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <SpendStat label="Last 30 days" value={totals.spent_30d_eur} testid="spent-30d" />
        <SpendStat label="Last 90 days" value={totals.spent_90d_eur} testid="spent-90d" />
        <SpendStat label="Last 12 months"
                   value={totals.spent_12m_eur}
                   warn={cumBreached}
                   testid="spent-12m" />
        <SpendStat label="Largest single"
                   value={totals.largest_single_eur}
                   warn={singleBreached}
                   testid="largest-single" />
      </div>
      {(cumBreached || singleBreached) && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2"
             data-testid="sof-threshold-warning">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>SoF trigger active.</strong>{" "}
            {cumBreached && (
              <span>12-month cumulative ≥ {formatEUR(t.cumulative_12m_eur)} (5AMLD enhanced due diligence).{" "}</span>
            )}
            {singleBreached && (
              <span>Single order ≥ {formatEUR(t.single_order_eur)}.{" "}</span>
            )}
            Verify Source-of-Funds documentation is on file before clearing further orders.
          </div>
        </div>
      )}
    </div>
  );
}

function SpendStat({ label, value, warn, testid }) {
  return (
    <div className={`p-3 rounded-xl border ${warn ? "border-amber-300 bg-amber-50" : "border-border bg-muted/20"}`}
         data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className={`font-display text-lg font-extrabold tabular-nums truncate ${warn ? "text-amber-900" : "text-foreground"}`}>
        {formatEUR(value || 0)}
      </div>
    </div>
  );
}

/* ----- SoF tab: review status + uploaded documents + signed-url download ----- */
function SofTab({ customerId, customer }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [reason, setReason] = useState("");

  const load = () => {
    setLoading(true);
    api.get(`/admin/sof/${customerId}/documents`)
      .then((r) => setDocs(r.data.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, [customerId]);

  const download = async (doc) => {
    try {
      const r = await api.get(`/admin/sof/documents/${doc.id}/signed-url`);
      // signed_url is relative to /api; open via REACT_APP_BACKEND_URL
      const base = process.env.REACT_APP_BACKEND_URL || "";
      window.open(`${base}${r.data.signed_url}`, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue signed URL");
    }
  };

  const review = async (decision) => {
    if (!window.confirm(`Mark SoF as ${decision} for this customer?`)) return;
    setReviewing(true);
    try {
      await api.post(`/admin/sof/${customerId}/review`, { decision, reason: reason || undefined });
      toast.success(`SoF ${decision}`);
      setReason("");
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setReviewing(false); }
  };

  const sofStatus = customer.sof_status || "not_required";
  const trig = customer.sof_trigger;
  const trigAmt = customer.sof_trigger_amount_eur;

  return (
    <div className="space-y-4">
      <div className="xch-card p-4" data-testid="sof-summary">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Source of Funds</div>
          <Badge variant="outline" className="text-[10px] uppercase font-bold" data-testid="sof-status-badge">
            {sofStatus.replace(/_/g, " ")}
          </Badge>
          {trig && (
            <span className="text-[11px] text-muted-foreground">
              Triggered by: <strong className="text-foreground">{trig}</strong>{trigAmt ? ` (${formatEUR(trigAmt)})` : ""}
            </span>
          )}
        </div>
        {sofStatus === "pending" && (
          <div className="flex flex-col gap-2 mt-2 md:flex-row md:items-center">
            <Input
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Optional reviewer note (sent to customer on reject)"
              className="rounded-xl flex-1" data-testid="sof-review-reason"
            />
            <div className="flex gap-2">
              <Button onClick={() => review("approved")} disabled={reviewing}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                      data-testid="sof-approve-btn">
                Approve
              </Button>
              <Button onClick={() => review("rejected")} disabled={reviewing} variant="outline"
                      className="rounded-xl border-rose-300 text-rose-700 hover:bg-rose-50"
                      data-testid="sof-reject-btn">
                Reject
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="xch-card overflow-hidden" data-testid="sof-docs-list">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            Uploaded documents · encrypted at rest
          </div>
          <Button onClick={load} variant="ghost" size="sm" className="rounded-md text-[11px]">Refresh</Button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No documents uploaded yet.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {docs.map((d) => (
              <div key={d.id} className="p-3 flex items-center gap-3" data-testid={`sof-doc-${d.id}`}>
                <FileText className="w-5 h-5 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-foreground truncate">
                    {d.label || d.filename}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate font-mono">
                    {d.filename} · {d.content_type} · {(d.size_plain / 1024).toFixed(1)} KB ·
                    {" "}{new Date(d.uploaded_at).toLocaleString("en-IE")}
                  </div>
                </div>
                <Button onClick={() => download(d)} variant="outline" size="sm"
                        className="rounded-xl gap-1.5"
                        data-testid={`sof-doc-${d.id}-download-btn`}>
                  <Download className="w-3.5 h-3.5" /> View
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
